import {getCookie} from '@github-ui/cookies'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {COOKIE_NAME as SSO_BANNER_COOKIE_NAME} from '@github-ui/global-sso-banner/constants'
import {lazyWithPreload} from '@github-ui/react-core/lazy-with-preload'
import {Link} from '@github-ui/react-core/link'
import {type PropsWithPartialAnchor, useExternalAnchor} from '@github-ui/react-core/react-partial-anchor'
import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {UserStatus} from '@github-ui/user-status-dialog'
import {Emoji} from '@github-ui/user-status-dialog/Emoji'
import {
  AccessibilityIcon,
  BeakerIcon,
  CodeSquareIcon,
  CopilotIcon,
  GearIcon,
  GlobeIcon,
  HeartIcon,
  type Icon,
  OrganizationIcon,
  PaintbrushIcon,
  PersonIcon,
  RepoIcon,
  ShieldIcon,
  SignOutIcon,
  SmileyIcon,
  StarIcon,
  UploadIcon,
} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, type AnchoredOverlayProps, Label, Stack, Tooltip, Truncate} from '@primer/react'
import {Dialog, type DialogHeaderProps, type DialogProps} from '@primer/react/experimental'
import {memo, type ReactNode, Suspense, useCallback, useMemo, useRef, useState} from 'react'

import {AccountSwitcher, type AccountSwitcherProps} from './AccountSwitcher'
import {analyticsCategory} from './constants'
import {ErrorDialog, type ErrorDialogProps} from './ErrorDialog'
import styles from './styles.module.css'
import {useEnterpriseTasksData} from './utils/use-enterprise-tasks-data'
import {type LazyLoadItemDataAttributes, useLazyData} from './utils/use-lazy-data'
import {useOrgTasksData} from './utils/use-org-tasks-data'

export function hasSSOBannerCookie() {
  const ssoCookie = getCookie(SSO_BANNER_COOKIE_NAME)
  return Boolean(ssoCookie?.value)
}

export interface GlobalNavUserMenuProps extends Omit<AccountSwitcherProps, 'stashedAccounts' | 'setError'> {
  owner: {
    login: string
    name: string
    avatarUrl: string
  }
  lazyLoadItemDataFetchUrl: string
  showAccountSwitcher: boolean
  showCopilot: boolean
  showEnterprises: boolean
  showEnterprise: boolean
  showGists: boolean
  showOrganizations: boolean
  showSponsors: boolean
  showUpgrade: boolean
  showFeaturesPreviews: boolean
  showEnterpriseSettings: boolean
  gistsUrl: string
  yourEnterpriseUrl: string
  enterpriseSettingsUrl: string
}

type UserStatusItemProps = {
  lazyLoadItemData?: LazyLoadItemDataAttributes
  onClick: () => void
}

const emptyAnalytics = {category: '', action: '', label: ''}

function NavLink({
  href,
  icon: NavIcon,
  children,
  className,
  analyticsAction,
  extraAnalytics = emptyAnalytics,
  extraOnClick,
}: {
  href: string
  icon: Icon
  className?: string
  extraAnalytics?: {
    category: string
    action: string
    label?: string
  }
  analyticsAction: string
  children: ReactNode

  /** click handler to fire in addition to the automatic analytics event built into NavLink */
  extraOnClick?: () => void
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const onClick = useCallback(() => {
    if (extraAnalytics.category) {
      sendClickAnalyticsEvent({
        category: extraAnalytics.category,
        action: extraAnalytics.action,
        label: extraAnalytics.label,
      })
    }

    sendClickAnalyticsEvent({
      category: analyticsCategory,
      action: analyticsAction,
    })

    extraOnClick?.()
  }, [
    extraAnalytics.category,
    extraAnalytics.action,
    extraAnalytics.label,
    sendClickAnalyticsEvent,
    analyticsAction,
    extraOnClick,
  ])

  return (
    <ActionList.LinkItem as={Link} to={href} onClick={onClick} className={className}>
      <ActionList.LeadingVisual>
        <NavIcon />
      </ActionList.LeadingVisual>
      {children}
    </ActionList.LinkItem>
  )
}

const UserStatusWrapper = ({children, hasStatusMessage}: {children: ReactNode; hasStatusMessage: boolean}) =>
  hasStatusMessage ? (
    <Tooltip direction="sw" delay="long" text="Edit status">
      {children}
    </Tooltip>
  ) : (
    children
  )

const UserStatusNavItem = memo(function UserStatusNavItem({lazyLoadItemData, onClick}: UserStatusItemProps) {
  // While in a loading state, explicitly label the button as "Set status" since it's still interactive
  const loadingAttributes = lazyLoadItemData ? undefined : {'aria-label': 'Set status', 'aria-labelledby': undefined}
  // check if the message is set to determine if a tooltip is needed
  const hasStatusMessage = (lazyLoadItemData?.userStatus?.messageHtml?.length ?? 0) > 0
  const [isLoading, setIsLoading] = useState(true)
  const [shouldShowLoading, setShouldShowLoading] = useState(false)

  const loadUserStatusDialog = useCallback(async () => {
    // Preload the user status dialog component
    await LazyUserStatusDialog.preload()
    setIsLoading(false)
  }, [])

  const onClickWrapper = useCallback(() => {
    setShouldShowLoading(true)
    onClick()
  }, [onClick])

  return (
    <UserStatusWrapper hasStatusMessage={hasStatusMessage}>
      <ActionList.Item
        {...testIdProps('global-user-nav-set-status-item')}
        {...loadingAttributes}
        onSelect={onClickWrapper}
        onMouseEnter={loadUserStatusDialog}
        onFocus={loadUserStatusDialog}
        loading={shouldShowLoading && isLoading}
      >
        <ActionList.LeadingVisual>
          {lazyLoadItemData?.userStatus?.emojiAttributes ? (
            <Emoji {...lazyLoadItemData?.userStatus.emojiAttributes} tone={lazyLoadItemData?.userPreferences?.tone} />
          ) : (
            <SmileyIcon />
          )}
        </ActionList.LeadingVisual>
        {lazyLoadItemData ? (
          <UnsafeHTMLDiv
            className={styles.emojiContainer}
            html={lazyLoadItemData.userStatus.messageHtml || 'Set status'}
          />
        ) : (
          <LoadingSkeleton height="md" />
        )}
      </ActionList.Item>
    </UserStatusWrapper>
  )
})

type UpgradeNavItemProps = {
  lazyLoadItemData?: LazyLoadItemDataAttributes
}

function UpgradeNavItem({lazyLoadItemData}: UpgradeNavItemProps) {
  const enterpriseTrialUrl = lazyLoadItemData?.enterpriseTrialUrl
  if (enterpriseTrialUrl) {
    return (
      <NavLink
        href={enterpriseTrialUrl}
        icon={UploadIcon}
        extraAnalytics={{
          category: 'start_a_free_trial',
          action: 'click_to_set_up_enterprise_trial',
          label: 'ref_loc:side_panel;ref_cta:try_enterprise',
        }}
        analyticsAction="TRY_ENTERPRISE"
      >
        Try Enterprise
        {/* eslint-disable-next-line primer-react/direct-slot-children */}
        <ActionList.TrailingVisual>
          <Label variant="primary">Free</Label>
        </ActionList.TrailingVisual>
      </NavLink>
    )
  } else {
    return (
      <NavLink href="/account/choose?action=upgrade" icon={UploadIcon} analyticsAction="UPGRADE_PLAN">
        Upgrade
      </NavLink>
    )
  }
}

/* This component will eventually use the Dialog component from Primer React to encapsulate the entire global user
 * nav menu. We aren't able to manage the dialog here because the User Menu opens several constituent dialogs -
 * the user status dialog and the feature preview dialog - which are currently rendered in Rails-land using the Primer
 * Dialog component from primer_view_components. It's architected this way because dialogs from the two frameworks
 * have proven to be somewhat incompatible. For example, pressing 'esc' closes the nav menu instead of the user
 * status dialog, etc. Keeping all the actual dialogs in Rails sidesteps these issues. When the constituent dialogs
 * are eventually ported to React, we can stick a Dialog in here and manage the entire nav menu in React.
 *
 * We've actually tried this already. See
 * https://github.com/github/github-ui/blob/999d5af6e063bbce5e676158d2c076d95cd2a565/packages/global-nav-user-menu/GlobalNavUserMenu.tsx
 * for inspiration when it's time to move the dialog into this component.
 */
export function GlobalNavUserMenu({
  lazyLoadItemDataFetchUrl,
  onClose: onCloseBase,
  owner,
  canAddAccount,
  addAccountPath,
  switchAccountPath,
  loginAccountPath,
  showAccountSwitcher,
  open,
  anchorRef,
  showGists,
  gistsUrl,
  showOrganizations,
  showSponsors,
  showUpgrade,
  showEnterprises,
  showEnterprise,
  yourEnterpriseUrl,
  showEnterpriseSettings,
  enterpriseSettingsUrl,
  showCopilot,
  showFeaturesPreviews,
}: GlobalNavUserMenuProps & {
  open: boolean
  onClose: DialogProps['onClose']
  anchorRef: React.RefObject<HTMLElement | null>
}) {
  const {data: lazyLoadItemData, updateUserStatus} = useLazyData(lazyLoadItemDataFetchUrl)
  const {data: lazyOrganizationNewTasksData} = useOrgTasksData()
  const {data: lazyEnterpriseNewTasksData} = useEnterpriseTasksData()

  const [showUserStatusDialog, setShowUserStatusDialog] = useState(false)
  const [showFeaturePreviewDialog, setShowFeaturePreviewDialog] = useState(false)
  const profilePath = `/${owner.login}`
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const onClose = useCallback<DialogProps['onClose']>(
    gesture => {
      onCloseBase(gesture)
    },
    [onCloseBase],
  )

  const openUserStatusDialog = useCallback(() => {
    setShowUserStatusDialog(true)
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'USER_STATUS'})
  }, [sendClickAnalyticsEvent])

  const onUserStatusClosed = useCallback(
    async (statusPromise?: Promise<UserStatus> | string) => {
      setShowUserStatusDialog(false)

      if (statusPromise && typeof statusPromise !== 'string') {
        try {
          updateUserStatus(await statusPromise)
        } catch {
          // Do nothing
        }
      }
    },
    [updateUserStatus],
  )

  const [isLoadingFeaturePreview, setIsLoadingFeaturePreview] = useState(true)
  const [shouldShowLoadingFeaturePreview, setShouldShowLoadingFeaturePreview] = useState(false)

  const openFeaturePreviewDialog = useCallback(() => {
    setShouldShowLoadingFeaturePreview(true)
    setShowFeaturePreviewDialog(true)
    sendClickAnalyticsEvent({category: analyticsCategory, action: 'FEATURE_PREVIEW'})
  }, [sendClickAnalyticsEvent])

  const loadFeaturePreviewDialog = useCallback(async () => {
    // Preload the feature preview dialog component
    await LazyFeaturePreviewDialog.preload()
    setIsLoadingFeaturePreview(false)
  }, [])

  const stashedAccounts = lazyLoadItemData?.stashedAccounts

  const [error, setError] = useState<ErrorDialogProps | false>(false)

  const renderHeader = useCallback(
    ({dialogLabelId}: DialogHeaderProps) => {
      return (
        <div className="d-flex tmp-pr-3 tmp-pl-3 tmp-pt-3" aria-label={owner.login} role="heading" aria-level={1}>
          <span className="sr-only" id={dialogLabelId}>
            User navigation
          </span>
          <div className="d-flex flex-1">
            <div className="d-flex">
              <GitHubAvatar src={owner.avatarUrl} size={32} />
              <div className="lh-condensed overflow-hidden d-flex flex-column flex-justify-center ml-2 f5 mr-auto">
                <div className="text-bold">
                  <Truncate title={owner.login} maxWidth={120}>
                    {owner.login}
                  </Truncate>
                </div>
                <div className="fgColor-muted">
                  <Truncate title={owner.name} maxWidth={120}>
                    {owner.name}
                  </Truncate>
                </div>
              </div>
            </div>
          </div>
          {showAccountSwitcher && (
            <AccountSwitcher
              canAddAccount={canAddAccount}
              addAccountPath={addAccountPath}
              switchAccountPath={switchAccountPath}
              stashedAccounts={stashedAccounts ?? null}
              loginAccountPath={loginAccountPath}
              setError={setError}
            />
          )}
          <div className="d-md-none">
            <Dialog.CloseButton onClose={() => onClose('close-button')} />
          </div>
        </div>
      )
    },
    [
      onClose,
      owner,
      stashedAccounts,
      canAddAccount,
      addAccountPath,
      switchAccountPath,
      loginAccountPath,
      showAccountSwitcher,
    ],
  )

  // See https://github.com/primer/react/issues/6563
  // Interacting with a nested `Dialog` component inside of an AnchoredOverlay causes both to be dismissed.
  // Track the refs for the launched dialogs and force ignore any interactions for their overlays
  const featurePreviewRef = useRef<HTMLDivElement>(null)
  const userStatusRef = useRef<HTMLDivElement>(null)
  const ignoreClickRefs = useMemo(() => [featurePreviewRef, userStatusRef], [featurePreviewRef, userStatusRef])

  if (!open) {
    return null
  }

  if (error) {
    return <ErrorDialog {...error} onClose={() => setError(false)} />
  }

  return (
    <ReductiveUserMenu
      onClose={reason => {
        if (showFeaturePreviewDialog) {
          // If the feature preview dialog is open, don't close the entire user menu
          return
        }

        if (showUserStatusDialog) {
          // If the user status dialog is open, don't close the entire user menu
          return
        }

        onClose(reason)
      }}
      renderHeader={renderHeader}
      anchorRef={anchorRef}
      ignoreClickRefs={ignoreClickRefs}
    >
      {showUserStatusDialog && (
        <Suspense fallback={null}>
          <LazyUserStatusDialog
            userStatusData={lazyLoadItemData?.userStatus}
            userPreferences={lazyLoadItemData?.userPreferences}
            ref={userStatusRef}
            onClose={onUserStatusClosed}
          />
        </Suspense>
      )}
      {showFeaturePreviewDialog && (
        <Suspense fallback={null}>
          <LazyFeaturePreviewDialog
            ref={featurePreviewRef}
            onClose={() => setShowFeaturePreviewDialog(false)}
            login={owner.login}
          />
        </Suspense>
      )}
      <Stack gap="condensed">
        <ActionList variant="full">
          <UserStatusNavItem lazyLoadItemData={lazyLoadItemData} onClick={openUserStatusDialog} />
          <SSONavLink lazyLoadItemData={lazyLoadItemData} />
          <ActionList.Divider />

          <CreateNewRepoItem />

          <NavLink href={profilePath} icon={PersonIcon} analyticsAction="PROFILE">
            Profile
          </NavLink>
          <NavLink href={`${profilePath}?tab=repositories`} icon={RepoIcon} analyticsAction="YOUR_REPOSITORIES">
            Repositories
          </NavLink>
          <NavLink href={`${profilePath}?tab=stars`} icon={StarIcon} analyticsAction="YOUR_STARS">
            Stars
          </NavLink>

          {showGists && (
            <NavLink href={gistsUrl} icon={CodeSquareIcon} analyticsAction="YOUR_GISTS">
              Gists
            </NavLink>
          )}
          {showOrganizations && (
            <NavLink
              href="/settings/organizations"
              icon={OrganizationIcon}
              analyticsAction="YOUR_ORGANIZATIONS"
              extraOnClick={() => {
                // Piggy back onto the NavLinks onClick, so that clicking either the visual button or just clicking the
                // NavLink normally will result in a Nudge analytics event in addition to the standard Nav analytics event
                if (lazyOrganizationNewTasksData?.enableOrganizationNewTasksExperiment) {
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: lazyOrganizationNewTasksData.showNewTasksButton
                      ? 'nav_organizations_new_tasks_nudge_1'
                      : 'nav_organizations_new_tasks_nudge_0',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                }
              }}
            >
              Organizations
              {lazyOrganizationNewTasksData?.enableOrganizationNewTasksExperiment &&
                (lazyOrganizationNewTasksData.showNewTasksButton ? (
                  /* eslint-disable-next-line primer-react/direct-slot-children */
                  <ActionList.TrailingVisual>
                    <span
                      {...testIdProps('analytics-data')}
                      data-analytics-visible='{"category":"nav_organizations_new_tasks_nudge_1","action":"visible","group":"engage"}'
                    />
                    <Label {...testIdProps('new-task-label')} variant="done">
                      New
                    </Label>
                  </ActionList.TrailingVisual>
                ) : (
                  <span
                    {...testIdProps('analytics-data')}
                    data-analytics-visible='{"category":"nav_organizations_new_tasks_nudge_0","action":"visible","group":"engage"}'
                  />
                ))}
            </NavLink>
          )}
          {showEnterprises && (
            <NavLink
              href="/settings/enterprises"
              icon={GlobeIcon}
              extraAnalytics={{
                category: 'enterprises_more_discoverable',
                action: 'click_your_enterprises',
                label: 'ref_loc:side_panel;ref_cta:your_enterprises;is_navigation_redesign:true',
              }}
              analyticsAction="YOUR_ENTERPRISES"
              extraOnClick={() => {
                // Piggy back onto the NavLinks onClick, so that clicking either the visual button or just clicking the
                // NavLink normally will result in a Nudge analytics event in addition to the standard Nav analytics event
                if (lazyEnterpriseNewTasksData?.enableEnterpriseNewTasksExperiment) {
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: lazyEnterpriseNewTasksData.showNewTasksButton
                      ? 'nav_enterprises_new_tasks_nudge_1'
                      : 'nav_enterprises_new_tasks_nudge_0',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                  // send our old analytics category which we used prior to October 2025
                  sendClickAnalyticsEvent({
                    location: 'global_user_nav_drawer',
                    category: 'dfd_nav_new_tasks_nudge_1',
                    action: 'new_task',
                    tag: 'a',
                    group: 'engage',
                  })
                }
              }}
            >
              Enterprises
              {lazyEnterpriseNewTasksData?.enableEnterpriseNewTasksExperiment &&
                (lazyEnterpriseNewTasksData.showNewTasksButton ? (
                  /* eslint-disable-next-line primer-react/direct-slot-children */
                  <ActionList.TrailingVisual>
                    <span
                      {...testIdProps('analytics-data')}
                      data-analytics-visible='{"category":"dfd_nav_new_tasks_nudge_1","action":"visible","group":"engage"}'
                    />
                    <span
                      {...testIdProps('analytics-data')}
                      data-analytics-visible='{"category":"nav_enterprises_new_tasks_nudge_1","action":"visible","group":"engage"}'
                    />
                    <Label {...testIdProps('new-task-label')} variant="done">
                      New
                    </Label>
                  </ActionList.TrailingVisual>
                ) : (
                  <span
                    {...testIdProps('analytics-data')}
                    data-analytics-visible='{"category":"nav_enterprises_new_tasks_nudge_0","action":"visible","group":"engage"}'
                  />
                ))}
            </NavLink>
          )}
          {showEnterprise && (
            <NavLink href={yourEnterpriseUrl} icon={GlobeIcon} analyticsAction="YOUR_ENTERPRISE">
              Enterprise
            </NavLink>
          )}
          {showSponsors && (
            <NavLink href="/sponsors/accounts" icon={HeartIcon} analyticsAction="SPONSORS">
              Sponsors
            </NavLink>
          )}

          <ActionList.Divider />

          <NavLink href="/settings/profile" icon={GearIcon} analyticsAction="SETTINGS">
            Settings
          </NavLink>

          {showEnterpriseSettings && (
            <NavLink href={enterpriseSettingsUrl} icon={GlobeIcon} analyticsAction="ENTERPRISE_SETTINGS">
              Enterprise settings
            </NavLink>
          )}

          {showCopilot && (
            <NavLink
              href="/settings/copilot"
              icon={CopilotIcon}
              extraAnalytics={{
                category: 'try_copilot',
                action: 'click_to_try_copilot',
                label: 'ref_loc:side_panel;ref_cta:your_copilot',
              }}
              analyticsAction="COPILOT_SETTINGS"
            >
              Copilot settings
            </NavLink>
          )}

          {showFeaturesPreviews && (
            <ActionList.Item
              onSelect={openFeaturePreviewDialog}
              onMouseEnter={loadFeaturePreviewDialog}
              onFocus={loadFeaturePreviewDialog}
              loading={shouldShowLoadingFeaturePreview && isLoadingFeaturePreview}
            >
              <ActionList.LeadingVisual>
                <BeakerIcon />
              </ActionList.LeadingVisual>
              {lazyLoadItemData?.hasUnseenFeatures && (
                <ActionList.TrailingVisual>
                  <Label variant="accent">New</Label>
                </ActionList.TrailingVisual>
              )}
              <span>Feature preview</span>
            </ActionList.Item>
          )}

          <NavLink href="/settings/appearance" icon={PaintbrushIcon} analyticsAction="APPEARANCE_SETTINGS">
            Appearance
          </NavLink>
          <NavLink href="/settings/accessibility" icon={AccessibilityIcon} analyticsAction="ACCESSIBILITY_SETTINGS">
            Accessibility
          </NavLink>

          {showUpgrade && <UpgradeNavItem lazyLoadItemData={lazyLoadItemData} />}

          <ActionList.Divider />

          <NavLink href="/logout" icon={SignOutIcon} analyticsAction="LOGOUT">
            Sign out
          </NavLink>
        </ActionList>
      </Stack>
    </ReductiveUserMenu>
  )
}

export function GlobalNavUserMenuPartial({
  reactPartialAnchor,
  ...props
}: PropsWithPartialAnchor<GlobalNavUserMenuProps>) {
  const {open, setOpen, ref: anchorRef} = useExternalAnchor(reactPartialAnchor)
  const onClose = useCallback(() => {
    setOpen(false)
    setTimeout(() => {
      // Dialog will soon support `returnFocusRef`, which should be used instead
      anchorRef.current?.focus()
    })
  }, [setOpen, anchorRef])

  return <GlobalNavUserMenu {...props} open={open} anchorRef={anchorRef} onClose={onClose} />
}

export function SSONavLink({lazyLoadItemData}: {lazyLoadItemData?: LazyLoadItemDataAttributes}) {
  // If we haven't loaded the SSO data yet, fallback to checking for a cookie to be optimistic
  // Since the cookie only is set if they dismiss the SSO banner, we can use it as a proxy for the user belonging to at least one SSO org

  if ((!lazyLoadItemData && hasSSOBannerCookie()) || lazyLoadItemData?.showSSO) {
    return (
      <NavLink href={lazyLoadItemData?.ssoUrl ?? '/settings/organizations'} icon={ShieldIcon} analyticsAction="SSO">
        Single sign-on
      </NavLink>
    )
  }

  return null
}

export function CreateNewRepoItem() {
  return (
    <>
      <NavLink className="hide-whenRegular" href={'/new'} icon={RepoIcon} analyticsAction="CREATE_NEW_REPOSITORY">
        Create new repository
      </NavLink>
      <ActionList.Divider className="hide-whenRegular" />
    </>
  )
}

function ReductiveUserMenu({
  onClose: onCloseDefault,
  anchorRef,
  ignoreClickRefs,
  renderHeader,
  children,
}: React.PropsWithChildren<{
  onClose: DialogProps['onClose']
  renderHeader: (props: React.PropsWithChildren<DialogHeaderProps>) => React.ReactElement
  ignoreClickRefs?: Array<React.RefObject<HTMLElement | null>>
  anchorRef: React.RefObject<HTMLElement | null>
}>) {
  const onClose = useCallback(
    (gesture: 'anchor-click' | 'click-outside' | 'escape' | 'close') => {
      if (gesture === 'anchor-click' || gesture === 'click-outside' || gesture === 'close') {
        onCloseDefault('close-button')
        return
      }

      onCloseDefault('escape')
    },
    [onCloseDefault],
  )

  const overlayProps = useMemo<AnchoredOverlayProps['overlayProps']>(() => {
    return {
      ignoreClickRefs: [anchorRef, ...(ignoreClickRefs ?? [])],
      overflow: 'auto',
      maxHeight: 'fit-content',
      role: 'dialog',
      'aria-labelledby': 'global-nav-user-menu-header',
      'aria-describedby': 'global-nav-user-menu-description',
    }
  }, [anchorRef, ignoreClickRefs])

  return (
    <AnchoredOverlay
      open
      onClose={onClose}
      renderAnchor={null}
      anchorRef={anchorRef}
      displayCloseButton={false}
      width="small"
      variant={{
        narrow: 'fullscreen',
        regular: 'anchored',
      }}
      focusZoneSettings={{
        activeDescendantFocus: true,
      }}
      overlayProps={overlayProps}
    >
      {renderHeader?.({
        dialogLabelId: 'global-nav-user-menu-header',
        dialogDescriptionId: 'global-nav-user-menu-description',
        onClose: onCloseDefault,
      })}
      <div className="p-2">{children}</div>
    </AnchoredOverlay>
  )
}

const LazyFeaturePreviewDialog = lazyWithPreload(async () => {
  const mod = await import('@github-ui/feature-preview-dialog')
  return {default: mod.FeaturePreviewDialog}
})

const LazyUserStatusDialog = lazyWithPreload(async () => {
  const mod = await import('@github-ui/user-status-dialog')
  return {default: mod.UserStatusDialog}
})
